import { useMemo } from 'react';
import { Bed, Bath, Maximize2, Star, Eye, ArrowRight, Palette, MapPin } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useNavigate } from 'react-router-dom';
import { trackEvent } from '@/lib/analytics';
import { formatCurrency } from '@/lib/utils';
import { useUnits } from '@/hooks/useUnits';
import type { UnitConfiguration, PropertyUnit } from '@/lib/units';

// Fallback residence types for when database data is unavailable
const residenceTypes = [
  {
    name: '1-Bedroom',
    type: 'Condo',
    beds: 1,
    baths: 1,
    sqft: '650-750',
    description: 'Perfect for professionals or couples seeking a low-maintenance luxury lifestyle.',
    category: '1bed' as const,
  },
  {
    name: '2-Bedroom',
    type: 'Condo',
    beds: 2,
    baths: 2,
    sqft: '950-1,150',
    description: 'Spacious living for small families or those who work from home.',
    category: '2bed' as const,
  },
  {
    name: '3-Bedroom',
    type: 'Townhome',
    beds: 3,
    baths: 2.5,
    sqft: '1,400-1,600',
    description: 'Multi-level townhomes with private outdoor spaces and premium finishes.',
    category: 'townhouse' as const,
  },
];

interface ResidenceCardData {
  id: string;
  name: string;
  type: string;
  beds: number;
  baths: number;
  sqft: string;
  priceRange?: string;
  description: string;
  category: '1bed' | '2bed' | 'townhouse';
  availableCount: number;
  hasVirtualTour: boolean;
  sampleImage?: string;
}

interface ResidencesProps {
  onAskSpecialist: () => void;
}

export const Residences = ({ onAskSpecialist }: ResidencesProps) => {
  const navigate = useNavigate();
  const { configurations, units, loading, error } = useUnits({ autoLoad: true });

  // Transform database configurations into display format grouped by category
  const residenceCards = useMemo<ResidenceCardData[]>(() => {
    if (loading || error || !configurations.length || !units.length) {
      // Fallback to hardcoded data
      return residenceTypes.map((type) => ({
        id: type.category,
        name: type.name,
        type: type.type,
        beds: type.beds,
        baths: type.baths,
        sqft: type.sqft,
        description: type.description,
        category: type.category,
        availableCount: 0,
        hasVirtualTour: false,
      }));
    }

    // Group configurations by category
    const configsByCategory: Record<'1bed' | '2bed' | 'townhouse', UnitConfiguration[]> = {
      '1bed': [],
      '2bed': [],
      'townhouse': [],
    };

    configurations.forEach((config) => {
      if (config.bedrooms === 1) {
        configsByCategory['1bed'].push(config);
      } else if (config.bedrooms === 2) {
        configsByCategory['2bed'].push(config);
      } else if (config.bedrooms >= 3) {
        configsByCategory['townhouse'].push(config);
      }
    });

    // Create residence cards for each category
    const cards: ResidenceCardData[] = [];

    (['1bed', '2bed', 'townhouse'] as const).forEach((category) => {
      const categoryConfigs = configsByCategory[category];
      if (categoryConfigs.length === 0) return;

      // Get all units matching configurations in this category
      const matchingUnits = units.filter((unit) =>
        categoryConfigs.some(
          (config) =>
            unit.block === config.block &&
            unit.unit_type === config.unit_type &&
            unit.bedrooms === config.bedrooms &&
            unit.bathrooms === config.bathrooms &&
            unit.study_room === (config.study_room === 1) &&
            unit.powder_rooms === config.powder_room &&
            (config.location === 'Canal View'
              ? unit.view_type === 'canal'
              : unit.view_type === 'lagoon')
        )
      );

      const availableUnits = matchingUnits.filter(
        (unit) => unit.availability === 'available'
      );

      // Calculate price range from available units with prices
      const unitsWithPrice = availableUnits.filter(
        (unit) => unit.show_price && unit.price
      );
      let priceRange: string | undefined;
      if (unitsWithPrice.length > 0) {
        const prices = unitsWithPrice.map((unit) => unit.price!);
        const minPrice = Math.min(...prices);
        const maxPrice = Math.max(...prices);
        if (minPrice === maxPrice) {
          priceRange = formatCurrency(minPrice);
        } else {
          priceRange = `${formatCurrency(minPrice)} - ${formatCurrency(maxPrice)}`;
        }
      }

      // Get representative configuration
      const representativeConfig = categoryConfigs[0];

      // Calculate sqft range
      const areas = categoryConfigs
        .map((c) => c.total_area_sq_ft)
        .filter((a) => a > 0);
      const minArea = Math.min(...areas);
      const maxArea = Math.max(...areas);
      const sqftRange =
        minArea === maxArea
          ? `${Math.round(minArea).toLocaleString()}`
          : `${Math.round(minArea).toLocaleString()}-${Math.round(maxArea).toLocaleString()}`;

      // Get sample image from available units
      const sampleUnit =
        availableUnits.find((unit) => unit.images.length > 0) || availableUnits[0];

      // Determine type (Condo vs Townhome)
      const type = category === 'townhouse' ? 'Townhome' : 'Condo';

      // Generate description based on category
      let description = '';
      if (category === '1bed') {
        description =
          'Perfect for professionals or couples seeking a low-maintenance luxury lifestyle.';
      } else if (category === '2bed') {
        description =
          'Spacious living for small families or those who work from home.';
      } else {
        description =
          'Multi-level townhomes with private outdoor spaces and premium finishes.';
      }

      // Check if any unit has virtual tour
      const hasVirtualTour = availableUnits.some(
        (unit) => unit.matterport_url || unit.kuula_url
      );

      cards.push({
        id: category,
        name: category === '1bed' ? '1-Bedroom' : category === '2bed' ? '2-Bedroom' : '3-Bedroom',
        type,
        beds: representativeConfig.bedrooms,
        baths: representativeConfig.bathrooms,
        sqft: sqftRange,
        priceRange,
        description,
        category,
        availableCount: availableUnits.length,
        hasVirtualTour,
        sampleImage: sampleUnit?.images[0],
      });
    });

    return cards;
  }, [configurations, units, loading, error]);

  const handlePropertyClick = (card: ResidenceCardData) => {
    trackEvent('property_card_click', { property: card.name, category: card.category });
    navigate(`/residences?view=featured&category=${card.category}`);
  };

  return (
    <section id="residences" className="py-20 lg:py-28 scroll-mt-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-3xl mb-16">
          <h2 className="font-display text-3xl lg:text-5xl font-semibold text-foreground mb-6">
            Residences
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Explore our luxury residences with interactive floor plans and 3D virtual tours. 
            Click on any residence to view detailed information.
          </p>
        </div>

        {/* Residence Image */}
        <div className="mb-16 rounded-2xl overflow-hidden shadow-luxury">
          <img
            src="/img/residence-interior.jpg"
            alt="Luxury residence interior at The Pearl"
            className="w-full h-[400px] lg:h-[500px] object-cover"
          />
        </div>

        {/* Residence Cards */}
        {loading ? (
          <div className="grid md:grid-cols-3 gap-8 lg:gap-10">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="border shadow-soft">
                <CardContent className="p-0">
                  <Skeleton className="h-48 w-full rounded-t-lg rounded-b-none" />
                  <div className="p-6">
                    <Skeleton className="h-6 w-32 mb-4" />
                    <Skeleton className="h-4 w-24 mb-2" />
                    <Skeleton className="h-4 w-40 mb-4" />
                    <div className="flex gap-6 mb-4">
                      <Skeleton className="h-4 w-16" />
                      <Skeleton className="h-4 w-16" />
                      <Skeleton className="h-4 w-20" />
                    </div>
                    <Skeleton className="h-20 w-full mb-6" />
                    <div className="flex gap-2">
                      <Skeleton className="h-9 flex-1" />
                      <Skeleton className="h-9 flex-1" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground mb-4">
              Unable to load residence data. Showing available options.
            </p>
          </div>
        ) : residenceCards.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground mb-4">
              No residences available at this time.
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-8 lg:gap-10">
            {residenceCards.map((card) => (
              <Card 
                key={card.id} 
                className="hover-lift border shadow-soft group transition-all duration-300 hover:shadow-luxury hover:-translate-y-2 cursor-pointer"
                onClick={() => handlePropertyClick(card)}
              >
                <CardContent className="p-0">
                  {/* Thumbnail Image */}
                  <div className="overflow-hidden rounded-t-lg">
                    <img
                      src={card.sampleImage || '/img/residence-interior.jpg'}
                      alt={`${card.name} residence interior`}
                      className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>

                  <div className="p-6">
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-display text-2xl font-semibold text-foreground group-hover:text-brand-teal transition-colors duration-300">
                          {card.name}
                        </h3>
                        {card.hasVirtualTour && (
                          <Badge variant="secondary" className="text-xs">
                            <Star className="w-3 h-3 mr-1" />
                            3D Tour
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-brand-teal font-medium">{card.type}</p>
                      {card.priceRange && (
                        <p className="text-sm font-semibold text-foreground mt-1">{card.priceRange}</p>
                      )}
                      {card.availableCount > 0 && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {card.availableCount} {card.availableCount === 1 ? 'unit' : 'units'} available
                        </p>
                      )}
                    </div>

                    <div className="flex items-center gap-6 mb-4 text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <Bed className="w-4 h-4" />
                        <span className="text-sm">{card.beds} bed</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Bath className="w-4 h-4" />
                        <span className="text-sm">{card.baths} bath</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Maximize2 className="w-4 h-4" />
                        <span className="text-sm">{card.sqft} sq ft</span>
                      </div>
                    </div>

                    <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                      {card.description}
                    </p>

                    <div className="flex flex-col gap-2">
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1 group-hover:bg-brand-teal group-hover:text-white group-hover:border-brand-teal transition-all duration-300"
                          onClick={(e) => {
                            e.stopPropagation();
                            trackEvent('property_card_cta', { property: card.name, action: 'view_details', category: card.category });
                            navigate(`/residences?view=featured&category=${card.category}`);
                          }}
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          View Property
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1 group-hover:bg-brand-teal group-hover:text-white group-hover:border-brand-teal transition-all duration-300"
                          onClick={(e) => {
                            e.stopPropagation();
                            trackEvent('property_card_cta', { property: card.name, action: 'ask_specialist', category: card.category });
                            onAskSpecialist();
                          }}
                        >
                          Ask Specialist
                        </Button>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full text-xs text-muted-foreground hover:text-brand-teal transition-colors duration-300"
                        onClick={(e) => {
                          e.stopPropagation();
                          trackEvent('interior_design_cta_click', { 
                            property: card.name,
                            location: 'residences_card',
                            category: card.category
                          });
                          // External URL - leave blank for now, will be configured later
                          const interiorDesignUrl = ''; // TODO: Add external URL
                          if (interiorDesignUrl) {
                            window.open(interiorDesignUrl, '_blank');
                          }
                        }}
                      >
                        <Palette className="w-3 h-3 mr-1" />
                        Interior Design Options
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <div className="mt-12 text-center">
          <Button
            size="lg"
            className="bg-brand-teal hover:bg-brand-navy text-white hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl mb-6"
            onClick={() => {
              trackEvent('cta_click', { location: 'residences', action: 'view_all_properties' });
              navigate('/residences');
            }}
          >
            View All Properties
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          <p className="text-sm text-muted-foreground italic mb-6">
            Pricing and availability to be announced. Register your interest to be notified first.
          </p>
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            <a
              href="/invest"
              onClick={(e) => {
                e.preventDefault();
                trackEvent('internal_link_click', { from: 'residences_section', to: '/invest' });
                navigate('/invest');
              }}
              className="text-brand-teal hover:text-brand-teal/80 underline transition-colors"
            >
              Investment Opportunities
            </a>
            <span className="text-muted-foreground">•</span>
            <a
              href="/amenities"
              onClick={(e) => {
                e.preventDefault();
                trackEvent('internal_link_click', { from: 'residences_section', to: '/amenities' });
                navigate('/amenities');
              }}
              className="text-brand-teal hover:text-brand-teal/80 underline transition-colors"
            >
              Explore Amenities
            </a>
          </div>
        </div>

        {/* Site Plan Banner */}
        <div 
          className="mt-16 relative rounded-2xl overflow-hidden group cursor-pointer"
          onClick={() => {
            trackEvent('cta_click', { location: 'residences_section', action: 'explore_site_plan' });
            navigate('/site-plan');
          }}
        >
          <img
            src="/img/sitemap-lander.jpg"
            alt="The Pearl at Grand Harbour - Interactive Site Plan showing lagoon, residences, marina and amenities"
            className="w-full block transition-transform duration-700 group-hover:scale-[1.02]"
          />
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          {/* Content overlay */}
          <div className="absolute inset-0 flex flex-col items-center justify-end pb-8 sm:pb-12 px-6 text-center">
            <div className="flex items-center gap-2 mb-3">
              <MapPin className="w-5 h-5 text-brand-sky-blue" />
              <span className="text-xs sm:text-sm font-medium tracking-widest uppercase text-brand-sky-blue">
                Development Overview
              </span>
            </div>
            <h3 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold text-white mb-3">
              Explore the Site Plan
            </h3>
            <p className="text-sm sm:text-base text-white/80 max-w-xl mb-6 leading-relaxed">
              Discover where your future home sits within the lagoon, marina, and world-class amenities. 
              Find the perfect position for your lifestyle.
            </p>
            <Button
              size="lg"
              className="bg-white text-brand-navy hover:bg-brand-sky-blue hover:text-brand-navy hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
              onClick={(e) => {
                e.stopPropagation();
                trackEvent('cta_click', { location: 'residences_site_plan_banner', action: 'explore_site_plan' });
                navigate('/site-plan');
              }}
            >
              <MapPin className="w-5 h-5 mr-2" />
              View Interactive Site Plan
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};
